import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import LangSwitcher from "@/components/LangSwitcher";
import { Button } from "@/components/ui/button";
import {
  HeartPulse, Baby, UserRound, BellRing, Megaphone, Languages,
  Database, ArrowRight, Phone, Mail, MapPin, CheckCircle2,
} from "lucide-react";
import ShareBar from "@/components/ShareBar";

export default function Landing() {
  const { t } = useApp();

  return (
    <div className="bg-[#FDFBF7] text-[#1A1D1A]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-[#E5EBE7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-9 h-9 rounded-xl bg-[#1E4631] grid place-items-center">
              <HeartPulse className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <span className="font-[Outfit] font-bold text-lg tracking-tight">KWIKILA Care</span>
            <span className="hidden sm:inline text-xs text-[#5C6B61] ml-2 italic">· La santé en confiance</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#5C6B61]">
            <a href="#features" className="hover:text-[#1E4631] transition" data-testid="nav-features">{t.nav.features}</a>
            <a href="#how" className="hover:text-[#1E4631] transition" data-testid="nav-how">{t.nav.how}</a>
            <a href="#impact" className="hover:text-[#1E4631] transition" data-testid="nav-impact">{t.nav.impact}</a>
            <Link to="/contact" className="hover:text-[#1E4631] transition" data-testid="nav-contact">{t.nav.contact}</Link>
          </nav>
          <div className="flex items-center gap-3">
            <LangSwitcher />
            <Link to="/pitch" className="hidden md:inline-flex text-sm font-medium text-[#5C6B61] hover:text-[#1E4631]" data-testid="nav-pitch">
              {t.nav.pitch}
            </Link>
            <Link to="/login" data-testid="login-link">
              <Button variant="outline" className="border-[#1E4631] text-[#1E4631] hover:bg-[#FDFBF7] rounded-lg font-medium">
                {t.nav.login}
              </Button>
            </Link>
            <Link to="/register-hospital" data-testid="register-link" className="hidden md:inline-block">
              <Button className="bg-[#D96C4A] hover:bg-[#B85A3D] text-white rounded-lg font-medium">
                {t.nav.register_hospital}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24 md:pt-28 md:pb-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-in">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E4631]/5 text-[#1E4631] text-xs font-semibold tracking-wide uppercase mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D96C4A]"></span>
              {t.hero.tag}
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-[Outfit] font-bold leading-[1.05] tracking-tight">
              {t.hero.title1}<br />
              <span className="brand-gradient-text">{t.hero.title2}</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C6B61] max-w-xl leading-relaxed">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/login" data-testid="hero-cta-primary">
                <Button className="bg-[#1E4631] hover:bg-[#163424] text-white rounded-lg px-7 py-6 text-base font-semibold">
                  {t.hero.cta_primary} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/register-hospital" data-testid="hero-cta-secondary">
                <Button className="bg-[#D96C4A] hover:bg-[#B85A3D] text-white rounded-lg px-7 py-6 text-base font-semibold">
                  {t.hero.cta_secondary}
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[#5C6B61]">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]"/> SMS auto</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]"/> 5 langues</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]"/> Confidentiel</div>
            </div>
          </div>
          <div className="relative fade-in delay-200">
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#D96C4A]/15 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-10 -right-6 w-40 h-40 bg-[#1E4631]/15 rounded-full blur-3xl"></div>
            <div className="relative rounded-3xl overflow-hidden border border-[#E5EBE7] shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1666886573553-6548db92db79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxhZnJpY2FuJTIwbW9kZXJuJTIwaG9zcGl0YWwlMjBkb2N0b3IlMjBwYXRpZW50fGVufDB8fHx8MTc3ODMyMjI4NHww&ixlib=rb-4.1.0&q=85"
                alt="Personnel médical d'un hôpital moderne en République Démocratique du Congo utilisant KWIKILA Care pour le suivi des patients"
                loading="eager"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3 border border-[#E5EBE7]">
                <div className="w-10 h-10 rounded-full bg-[#2E7D32]/10 grid place-items-center">
                  <BellRing className="w-5 h-5 text-[#2E7D32]"/>
                </div>
                <div className="text-sm">
                  <div className="font-semibold text-[#1A1D1A]">Rappel envoyé</div>
                  <div className="text-[#5C6B61]">+243 9XX XXX · J-3 · RDV obstétrique</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-20 md:py-28 border-y border-[#E5EBE7] bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold uppercase tracking-widest text-[#D96C4A] mb-4">{t.problem.kicker}</div>
          <h2 className="text-4xl md:text-5xl font-[Outfit] font-bold leading-tight max-w-3xl">{t.problem.title}</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-8 text-[#5C6B61] text-lg leading-relaxed">
            <p>{t.problem.p1}</p>
            <p>{t.problem.p2}</p>
          </div>
        </div>
      </section>

      {/* Features (Bento) */}
      <section id="features" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold uppercase tracking-widest text-[#D96C4A] mb-4">{t.features.kicker}</div>
          <h2 className="text-4xl md:text-5xl font-[Outfit] font-bold leading-tight max-w-3xl">{t.features.title}</h2>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <FeatureCard
              testid="feat-pregnant"
              tone="accent"
              icon={<Baby className="w-6 h-6"/>}
              title={t.features.pregnant.t}
              desc={t.features.pregnant.d}
              image="https://images.unsplash.com/photo-1737649215354-6151b2cef0d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxhZnJpY2FuJTIwcHJlZ25hbnQlMjB3b21hbiUyMHNtaWxpbmd8ZW58MHx8fHwxNzc4MzIyMjk1fDA&ixlib=rb-4.1.0&q=85"
            />
            <FeatureCard
              testid="feat-elderly"
              tone="primary"
              icon={<UserRound className="w-6 h-6"/>}
              title={t.features.elderly.t}
              desc={t.features.elderly.d}
              image="https://images.unsplash.com/photo-1603085356448-6857558a32b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwZWxkZXJseSUyMG1hbiUyMHNtaWxpbmclMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzgzMjIyOTl8MA&ixlib=rb-4.1.0&q=85"
            />
            <FeatureCard testid="feat-reminders" icon={<BellRing className="w-6 h-6"/>} title={t.features.reminders.t} desc={t.features.reminders.d} />
            <FeatureCard testid="feat-campaigns" icon={<Megaphone className="w-6 h-6"/>} title={t.features.campaigns.t} desc={t.features.campaigns.d} />
            <FeatureCard testid="feat-multilingual" icon={<Languages className="w-6 h-6"/>} title={t.features.multilingual.t} desc={t.features.multilingual.d} />
            <FeatureCard testid="feat-registry" icon={<Database className="w-6 h-6"/>} title={t.features.registry.t} desc={t.features.registry.d} />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-20 md:py-28 bg-[#1E4631] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold uppercase tracking-widest text-[#D96C4A] mb-4">{t.how.kicker}</div>
          <h2 className="text-4xl md:text-5xl font-[Outfit] font-bold leading-tight max-w-3xl">{t.how.title}</h2>
          <div className="mt-14 grid md:grid-cols-4 gap-6">
            {[t.how.s1, t.how.s2, t.how.s3, t.how.s4].map((s, i) => (
              <div key={i} data-testid={`step-${i+1}`} className="relative p-7 bg-white/5 backdrop-blur rounded-2xl border border-white/10 hover:bg-white/10 transition">
                <div className="text-5xl font-[Outfit] font-bold text-[#D96C4A]">{String(i+1).padStart(2,'0')}</div>
                <div className="mt-4 font-semibold text-lg">{s.t}</div>
                <div className="mt-2 text-sm text-white/70 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section id="impact" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold uppercase tracking-widest text-[#D96C4A] mb-4">{t.impact.kicker}</div>
          <h2 className="text-4xl md:text-5xl font-[Outfit] font-bold leading-tight max-w-3xl">{t.impact.title}</h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { v: t.impact.stat1, l: t.impact.stat1l },
              { v: t.impact.stat2, l: t.impact.stat2l },
              { v: t.impact.stat3, l: t.impact.stat3l },
              { v: t.impact.stat4, l: t.impact.stat4l },
            ].map((s, i) => (
              <div key={i} data-testid={`stat-${i}`} className="p-8 rounded-2xl bg-white border border-[#E5EBE7] hover:border-[#D96C4A] transition">
                <div className="text-4xl md:text-5xl font-[Outfit] font-bold text-[#1E4631]">{s.v}</div>
                <div className="mt-2 text-sm font-medium text-[#5C6B61]">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl bg-gradient-to-br from-[#1E4631] to-[#163424] p-10 md:p-16 text-white relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#D96C4A]/20 rounded-full blur-3xl"></div>
            <div className="relative">
              <h3 className="text-3xl md:text-5xl font-[Outfit] font-bold leading-tight max-w-2xl">{t.cta_section.title}</h3>
              <p className="mt-5 text-white/80 max-w-xl text-lg">{t.cta_section.desc}</p>
              <div className="mt-8 flex flex-wrap gap-6 text-sm text-white/80">
                <span className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#D96C4A]"/> +243 824 314 459</span>
                <span className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#D96C4A]"/> contact@kwikilacare.com</span>
                <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D96C4A]"/> Kinshasa, RDC</span>
              </div>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/register-hospital" data-testid="cta-register-btn">
                  <Button className="bg-[#D96C4A] hover:bg-[#B85A3D] text-white rounded-lg px-8 py-6 text-base font-semibold">
                    {t.cta_section.btn} <ArrowRight className="ml-2 w-4 h-4"/>
                  </Button>
                </Link>
                <Link to="/pitch" data-testid="cta-pitch-btn">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1E4631] rounded-lg px-8 py-6 text-base font-semibold bg-transparent">
                    {t.cta_section.btn2}
                  </Button>
                </Link>
                <Link to="/contact" data-testid="cta-contact-btn">
                  <Button variant="outline" className="border-white/40 text-white hover:bg-white hover:text-[#1E4631] rounded-lg px-8 py-6 text-base font-semibold bg-transparent">
                    Nous écrire
                  </Button>
                </Link>
              </div>

              {/* Social share */}
              <div className="mt-12 pt-8 border-t border-white/15">
                <ShareBar
                  url="https://kwikilacare.com/"
                  variant="dark"
                  label="Partagez KWIKILA Care avec un hôpital"
                  text="Découvrez KWIKILA Care : la plateforme numérique de santé pour les hôpitaux en RDC. Gestion patients, rappels SMS automatiques, conseils IA en 5 langues locales, rapports au Ministère de la Santé."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5EBE7] py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[#5C6B61]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#1E4631] grid place-items-center">
              <HeartPulse className="w-4 h-4 text-white"/>
            </div>
            <span className="font-semibold text-[#1A1D1A]">KWIKILA Care</span>
            <span>· {t.footer.made}</span>
          </div>
          <div>© 2026 · {t.footer.rights}</div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ testid, icon, title, desc, image, tone }) {
  return (
    <div data-testid={testid} className="group bg-white rounded-2xl border border-[#E5EBE7] p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
      {image && (
        <div className="mb-5 rounded-xl overflow-hidden h-40">
          <img src={image} alt={title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        </div>
      )}
      <div className={`w-11 h-11 rounded-xl grid place-items-center mb-4 ${
        tone === "accent" ? "bg-[#D96C4A]/10 text-[#D96C4A]" :
        tone === "primary" ? "bg-[#1E4631]/10 text-[#1E4631]" :
        "bg-[#1E4631]/5 text-[#1E4631]"
      }`}>{icon}</div>
      <h3 className="font-[Outfit] font-semibold text-xl text-[#1A1D1A]">{title}</h3>
      <p className="mt-2 text-sm text-[#5C6B61] leading-relaxed">{desc}</p>
    </div>
  );
}
